import { DictionaryRecordContextProvider } from "@/components/dictionaries/DictionaryContext";
import DictionaryLoader from "@/components/dictionaries/DictionaryLoader";
import DictionaryPageHeader from "@/components/dictionaries/DictionaryPageHeader";
import { useToast } from "@/components/ui/use-toast";
import { Module } from "@/constants";
import { DictionaryContext } from "@/containers/dictionaries/types";
import { useQueryCacheUpdater } from "@/hooks/useQueryCacheUpdater";
import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import ShiftReportDepartmentForm from "@/modules/shiftReportDepartments/components/ShiftReportDepartmentForm";
import { useShiftReportDepartmentsDictionary } from "@/modules/shiftReportDepartments/hooks/useShiftReportDepartmentsDictionary";
import { ApiError } from "@/services/api";
import {
  ReadShiftReportDepartment,
  WriteShiftReportDepartment,
} from "@/types/shiftReportDepartment";
import { zodResolver } from "@hookform/resolvers/zod";
import { MutationFunction, useMutation, useQuery } from "@tanstack/react-query";
import { exists } from "i18next";
import { useEffect } from "react";
import { DefaultValues, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";

export default function ShiftReportDepartmentEditor({ id }: DictionaryContext) {
  const { t } = useTranslation();
  const config = useShiftReportDepartmentsDictionary();
  const navigate = useNavigate();
  const { toast } = useToast();
  const labels = config.getLabels(t);
  const { updateQueryDataForExistingItem } = useQueryCacheUpdater(
    Module.ShiftReportDepartments,
  );
  const { can_update } = useModulePermissions();
  const query = useQuery<ReadShiftReportDepartment>({
    queryKey: [config.ns, config.id, id],
    queryFn: async () => {
      const result = await config.service.get(id);
      return result as ReadShiftReportDepartment;
    },
  });

  const mutationFn: MutationFunction<
    ReadShiftReportDepartment,
    WriteShiftReportDepartment
  > = async (values) => config.service.patch(id, values);

  const mutation = useMutation<
    ReadShiftReportDepartment,
    ApiError,
    WriteShiftReportDepartment
  >({
    mutationFn,
    onSuccess: (response) => {
      const id = response.id;
      navigate(`${config.path}/${id}`);
      toast({
        title: labels.success.create,
      });
      updateQueryDataForExistingItem(response);
    },
    onError: ({ errorCode }) => {
      const description = exists(`errors.${errorCode}`)
        ? t(`errors.${errorCode}`)
        : t("errors.unknownError");
      toast({
        title: labels.errors.create,
        description,
        variant: "destructive",
      });
    },
  });

  const form = useForm<WriteShiftReportDepartment>({
    resolver: zodResolver(config.schema),
    defaultValues: (config.defaultValues ||
      {}) as DefaultValues<WriteShiftReportDepartment>,
    disabled: mutation.isPending || !can_update,
  });

  useFormUnsavedChangesWarning(form);

  useEffect(() => {
    if (query.data) {
      const formValues = Object.fromEntries(
        Object.entries(query.data).filter(([, value]) => value != null),
      ) as DefaultValues<WriteShiftReportDepartment>;
      form.reset(formValues);
    }
  }, [form, query.data]);

  return (
    <DictionaryRecordContextProvider
      value={{
        query,
        config,
        mutation,
        form,
        id,
      }}
    >
      <DictionaryPageHeader />
      {query.isLoading && <DictionaryLoader />}
      {!query.isLoading && <ShiftReportDepartmentForm />}
    </DictionaryRecordContextProvider>
  );
}
